import { FunctionComponent, useCallback, useState, PropsWithChildren } from 'react';
import { useMutation } from '@apollo/client';
import { gql } from '__generated__/gql';
import { useExtendedIntl } from 'hooks/useExtendedIntl';
import Delete from '@mui/icons-material/Delete';
import { MiniDialog } from 'shared/MiniDialog';
import { ProgressIconButton } from 'shared/ProgressButtons/ProgressIconButton';
import { ProgressButton } from 'shared/ProgressButtons/ProgressButton';
import { GraphQLObject, Connection, Node } from 'typeDeclarations/graphql/base-types';
import { CACHED_BILLING_PROFILE_QUERY, CachedBillingProfileSessionTeamData } from 'shared/PaymentSources/sharedQueries';
import { UserNode } from 'typeDeclarations/graphql/nodes';
import { STRIPE_PAYMENT_SOURCES_PAGE_SIZE } from 'shared/PaymentSources/constants';
import { PaymentSourceUnionNode } from 'shared/PaymentSources/sharedFragments';
import { useDefaultOnError } from 'hooks/useDefaultOnError';
import { ButtonProps, IconButtonProps } from '@mui/material';

interface DeletePaymentSourceTeamPayload extends GraphQLObject {
  team: Node & {
    modified: string;
    billingProfile: Node & {
      modified: string;
      otherStripeCardPaymentSources: Connection<Node & { modified: string } & PaymentSourceUnionNode>;
    };
  };
}

interface DeletePaymentSourceUserPayload extends GraphQLObject {
  user: Pick<UserNode, 'id' | 'modified' | 'numStripeCardPaymentSources' | 'numStripeCardPaymentSourcesMax'>;
}

interface DeleteStripePaymentSourceMutationData extends GraphQLObject {
  sessionTeam: DeletePaymentSourceTeamPayload;
  sessionUser: DeletePaymentSourceUserPayload;
}

interface DeletePaymentSourceMutationVariables {
  amount: number;
  cursor: string | null;
  input: {
    paymentSourceId: string;
    force: boolean;
  };
}

const DELETE_PAYMENT_SOURCE_MUTATION = gql(/* GraphQL */ `
  mutation deletePaymentSourceMutation($input: DeletePaymentSourceInput!, $amount: Int!, $cursor: String) {
    deletePaymentSource(input: $input) {
      clientMutationId
    }
    sessionTeam(input: {}) {
      team {
        id
        modified
        billingProfile {
          id
          modified
          ...PaymentSourceMutationBillingProfileFragment
        }
      }
    }
    sessionUser(input: {}) {
      user {
        id
        modified
        numStripeCardPaymentSources
        numStripeCardPaymentSourcesMax
      }
    }
  }
`);

interface DeletePaymentSourceButtonBaseProps {
  paymentSourceId: string;
  primary?: boolean;
}

interface DeletePaymentSourceButtonPropsWithIcon
  extends DeletePaymentSourceButtonBaseProps, Omit<IconButtonProps, 'children' | 'loading'> {
  iconButton: true;
}

interface DeletePaymentSourceButtonPropsWithButton
  extends DeletePaymentSourceButtonBaseProps, Omit<ButtonProps, 'loading'> {
  iconButton?: false;
}

type DeletePaymentSourceButtonProps = DeletePaymentSourceButtonPropsWithIcon | DeletePaymentSourceButtonPropsWithButton;

export const DeletePaymentSourceButton: FunctionComponent<PropsWithChildren<DeletePaymentSourceButtonProps>> = (
  props,
) => {
  const { paymentSourceId, primary = false } = props;
  const { formatMessage } = useExtendedIntl();
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const onError = useDefaultOnError();

  const handleOnCompleted = useCallback(() => {
    if (primary) {
      setDialogOpen(false);
    }
  }, [primary]);

  const [deleteStripePaymentSourceMutation, { client, loading: loadingRaw }] = useMutation<
    DeleteStripePaymentSourceMutationData,
    DeletePaymentSourceMutationVariables
  >(DELETE_PAYMENT_SOURCE_MUTATION, {
    onError: (err) => onError(err),
    onCompleted: handleOnCompleted,
  });

  const deleteStripePaymentSource = useCallback(() => {
    const cachedData = client.readQuery<CachedBillingProfileSessionTeamData>({
      query: CACHED_BILLING_PROFILE_QUERY,
    });

    if (!cachedData) {
      onError(new Error('No cachedBillingProfileData for the deleteStripePaymentSource mutation.'));
      return;
    }

    deleteStripePaymentSourceMutation({
      variables: {
        amount: STRIPE_PAYMENT_SOURCES_PAGE_SIZE,
        cursor: null,
        input: {
          paymentSourceId,
          force: true,
        },
      },
    });
  }, [client, deleteStripePaymentSourceMutation, onError, paymentSourceId]);
  const loading = loadingRaw === true;

  return (
    <>
      {props.iconButton
        ? (() => {
            const { iconButton, paymentSourceId, primary, ...iconButtonProps } = props;
            void iconButton;
            void paymentSourceId;
            void primary;

            return (
              <ProgressIconButton onClick={handleDialog} loading={loading} {...iconButtonProps}>
                <Delete />
              </ProgressIconButton>
            );
          })()
        : (() => {
            const { iconButton, paymentSourceId, primary, ...buttonProps } = props;
            void iconButton;
            void paymentSourceId;
            void primary;

            return (
              <ProgressButton onClick={handleDialog} loading={loading} variant="text" {...buttonProps}>
                {formatMessage({ id: 'shared.delete' })}
              </ProgressButton>
            );
          })()}
      <MiniDialog
        variant="delete"
        open={dialogOpen}
        loading={loading}
        onClose={handleCloseDialog}
        onClick={deleteStripePaymentSource}
        title={`${formatMessage({ id: 'shared.delete' })}?`}
        primaryButtonText={formatMessage({ id: 'shared.delete' })}
        secondaryButtonText={formatMessage({ id: 'shared.cancel' })}
        description={formatMessage({ id: 'shared.warning.delete' })}
      />
    </>
  );
};
